"use client";
import {
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  Logo,
  TextField,
} from "@/components";
import { useDepositToIbanInquiryMutation } from "@/hooks/useInquiry";
import {
  IServiceFormProps,
  TAccountNumber,
  TRawServiceApiKeys,
} from "@/interfaces";
import { cn, toEnglishDigits } from "@/lib";
import { useAppStore, useModalStore, useSecureStore } from "@/store";
import { accountNumberSchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Dropdown from "../../ui/dropdown/dropdown";
import { servicesQuickAccessNavigation } from "@/constants";
import InquiryCost from "../../inquiryCost";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useGetBanks } from "@/hooks";
import HtmlRenderer from "../../htmlRenderer";
import MainLogo from "@public/svg/logo-2.svg";
import Loader from "@/components/ui/loader";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { IDepositToIbanInquiryMutationPayload } from "@/interfaces/inquiries/bank";
import SampleInquiryModal from "@/components/inquirySamples/sampleInquiryModal";
import AccountToIbanSample from "@/components/inquirySamples/accountToIbanSample";

const AccountToIbanForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  serviceId,
}: IServiceFormProps) => {
  const t = useTranslations();
  const { data: session } = useSession();
  const { persistedFields } = useSecureStore();
  const { setPostLoginAction } = useAppStore();
  const {
    isPending: isSubmitting,
    mutate: depositToIbanInquiryFn,
    error: depositToIbanInquiryError,
    reset: resetDepositToIbanInquiry,
  } = useDepositToIbanInquiryMutation(session?.user.token);
  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const openPaymentModal = () => {
    setOpenModal("payment-method");
  };
  const [packageId, setPackageId] = useState<string | null>(null);
  // Handle package selection
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const serviceKey = TRawServiceApiKeys.Account_To_Iban;
  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    setValue,
    getValues,
  } = useForm<TAccountNumber>({
    resolver: zodResolver(accountNumberSchema),
    defaultValues: {
      number: persistedFields?.[serviceKey]?.number || "",
      bank: persistedFields?.[serviceKey]?.bank || "",
    },
  });

  const onSubmit = (data: any) => {
    setError(null);
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSampleOpen, setIsSampleOpen] = useState(false);
  const paymentSubmission = () => {
    const formData = getValues();

    const payload: Readonly<IDepositToIbanInquiryMutationPayload> = {
      number: toEnglishDigits(formData.number),
      bank: selectedBank,
      campaign: packageId || undefined,
    };

    depositToIbanInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };
  const bankOptions = useGetBanks().data?.data;
  const [selectedBank, setSelectedBank] = useState(
    persistedFields?.[serviceKey]?.bank || "",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetDepositToIbanInquiry();
    }
  }, [isPaymentMethodModalOpen]);

  if (!bankOptions) return null;
  const filteredBanks = bankOptions.filter((bank) =>
    bank.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const initialBankLabel =
    bankOptions.find((bank) => bank.key === persistedFields?.[serviceKey]?.bank)
      ?.label || "";

  const activeKey = servicesQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Account_To_Iban.key,
  )!;
  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className={cn("w-full")}>
          <div className="mb-8 flex w-full max-w-[80%] flex-col items-center justify-center gap-8">
            <fieldset className="size-full">
              <Controller
                name="bank"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    items={filteredBanks.map((bank) => bank.label)}
                    value={
                      selectedBank
                        ? bankOptions.find((bank) => bank.key === selectedBank)
                            ?.label || ""
                        : initialBankLabel
                    }
                    onChange={(label) => {
                      const bank = bankOptions.find(
                        (bank) => bank.label === label,
                      );
                      if (bank) {
                        setSelectedBank(bank.key);
                        field.onChange(bank.key);
                      }
                    }}
                    searchable={true}
                    onSearch={setSearchQuery}
                    searchValue={searchQuery}
                    variant="shadow"
                    placeholder={t(
                      "serviceFormSubmissions.accountToIban.bankPlaceholder",
                    )}
                    className="size-full! h-10! lg:h-10! xl:h-12!"
                  />
                )}
              />
              {formError.bank && (
                <p className="text-error-main mt-2 text-xs">
                  {t(formError.bank.message as any)}
                </p>
              )}
            </fieldset>
            <fieldset className="size-full">
              <Controller
                name={"number"}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    hasShadow
                    numeric
                    inputMode="numeric"
                    aria-label="شماره حساب"
                    maxLength={16}
                    placeholder={t(
                      "serviceFormSubmissions.accountToIban.accountPlaceholder",
                    )}
                    inputClassName={cn("tracking-widest h-10")}
                    error={formError.number?.message}
                  />
                )}
              />
            </fieldset>
          </div>
          {session?.user.token && <InquiryCost cost={cost} />}
          {announcement && <HtmlRenderer htmlString={announcement} />}
          <FlexBetween gap={2} className="lg:gap-4">
            {/*<CustomButton*/}
            {/*  id="account-to-iban-sample"*/}
            {/*  type="button"*/}
            {/*  className="border-primary-main text-primary-main hover:bg-primary-main/5 border bg-transparent !px-3 !text-sm"*/}
            {/*  onClick={() => setIsSampleOpen(true)}*/}
            {/*>*/}
            {/*  نمونه نتیجه*/}
            {/*</CustomButton>*/}
            <CustomButton
              id="account-to-iban-submit"
              disabled={disableService}
              className="flex-1"
              variant="primary"
              type="submit"
            >
              {t("serviceFormSubmissions.accountToIban.confirmButton")}
            </CustomButton>
          </FlexBetween>
        </FlexBetweenCol>
      </form>
      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-account-to-iban"
          submissionError={depositToIbanInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          submissionLoading={isSubmitting}
          onErrorReset={resetDepositToIbanInquiry}
          onPackageSelect={handlePackageSelect}
          serviceId={serviceId}
          showData="service"
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
      {isSampleOpen && (
        <SampleInquiryModal
          onClose={() => setIsSampleOpen(false)}
          title="نمونه نتیجه استعلام حساب به شبا"
        >
          <AccountToIbanSample />
        </SampleInquiryModal>
      )}
      {isSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountToIbanForm;
